<template>
  <div class="w-full">
    <div class="mb-4">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-medium">
          Billing Address
        </h3>
        <div
          v-if="isEditing"
          class="flex items-center gap-2"
        >
          <InputSwitch
            id="same_as_address"
            v-model="sameAsAddress"
            @update:model-value="handleSameAsAddressToggle"
          />
          <label
            for="same_as_address"
            class="text-sm text-gray-700 cursor-pointer"
          >Same as Address</label>
        </div>
      </div>
      <p class="text-sm text-gray-500">
        This address will be used for billing and invoicing purposes.
      </p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="p-2">
        <label
          for="billing_address_1"
          class="block text-gray-700 mb-2 font-medium"
        >Address Line 1</label>
        <InputText
          id="billing_address_1"
          :model-value="company.billing_address?.address_1 || ''"
          :disabled="!isEditing || sameAsAddress"
          class="w-full"
          placeholder="Enter address line 1"
          @update:model-value="updateBillingAddressField('address_1', $event)"
        />
      </div>
      <div class="p-2">
        <label
          for="billing_address_2"
          class="block text-gray-700 mb-2 font-medium"
        >Address Line 2</label>
        <InputText
          id="billing_address_2"
          :model-value="company.billing_address?.address_2 || ''"
          :disabled="!isEditing || sameAsAddress"
          class="w-full"
          placeholder="Enter address line 2"
          @update:model-value="updateBillingAddressField('address_2', $event)"
        />
      </div>
      <div class="p-2">
        <label
          for="billing_city"
          class="block text-gray-700 mb-2 font-medium"
        >City</label>
        <InputText
          id="billing_city"
          :model-value="company.billing_address?.city || ''"
          :disabled="!isEditing || sameAsAddress"
          class="w-full"
          placeholder="Enter city"
          @update:model-value="updateBillingAddressField('city', $event)"
        />
      </div>
      <div class="p-2">
        <label
          for="billing_state"
          class="block text-gray-700 mb-2 font-medium"
        >State/County</label>
        <InputText
          id="billing_state"
          :model-value="company.billing_address?.state || ''"
          :disabled="!isEditing || sameAsAddress"
          class="w-full"
          placeholder="Enter state or county"
          @update:model-value="updateBillingAddressField('state', $event)"
        />
      </div>
      <div class="p-2">
        <label
          for="billing_postcode"
          class="block text-gray-700 mb-2 font-medium"
        >Postcode</label>
        <InputText
          id="billing_postcode"
          :model-value="company.billing_address?.postcode || ''"
          :disabled="!isEditing || sameAsAddress"
          class="w-full"
          placeholder="Enter postcode"
          @update:model-value="updateBillingAddressField('postcode', $event)"
        />
      </div>
      <div class="p-2">
        <label
          for="billing_country"
          class="block text-gray-700 mb-2 font-medium"
        >Country</label>
        <InputText
          id="billing_country"
          :model-value="company.billing_address?.country || ''"
          :disabled="!isEditing || sameAsAddress"
          class="w-full"
          placeholder="Enter country"
          @update:model-value="updateBillingAddressField('country', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
    company: {
        type: Object,
        required: true
    },
    isEditing: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:company']);

// Check if billing address is same as address
const sameAsAddress = ref(false);

// Check if billing address matches address
const checkIfSameAsAddress = () => {
    if (!props.company.address || !props.company.billing_address) {
        sameAsAddress.value = false;
        return;
    }
    
    const address = props.company.address;
    const billingAddress = props.company.billing_address;
    
    sameAsAddress.value = (
        (address.address_1 || '') === (billingAddress.address_1 || '') &&
        (address.address_2 || '') === (billingAddress.address_2 || '') &&
        (address.city || '') === (billingAddress.city || '') &&
        (address.state || '') === (billingAddress.state || '') &&
        (address.postcode || '') === (billingAddress.postcode || '') &&
        (address.country || '') === (billingAddress.country || '')
    );
};

// Handle toggle switch
const handleSameAsAddressToggle = (value) => {
    sameAsAddress.value = value;
    
    if (value) {
        // Copy address to billing_address
        const updatedCompany = {
            ...props.company,
            billing_address: {
                address_1: props.company.address?.address_1 || '',
                address_2: props.company.address?.address_2 || '',
                city: props.company.address?.city || '',
                state: props.company.address?.state || '',
                postcode: props.company.address?.postcode || '',
                country: props.company.address?.country || ''
            }
        };
        
        emit('update:company', updatedCompany);
    }
};

const updateBillingAddressField = (field, value) => {
    // If same as address is enabled, disable it when user manually edits
    if (sameAsAddress.value) {
        sameAsAddress.value = false;
    }
    
    const updatedCompany = { 
        ...props.company, 
        billing_address: { 
            ...(props.company.billing_address || {}), 
            [field]: value 
        } 
    };
    
    emit('update:company', updatedCompany);
};

// Initialize toggle state on mount
onMounted(() => {
    checkIfSameAsAddress();
});
</script>
